import { formatDkDate } from '@portal/platform/format';

/**
 * Cell formatting by format token, ported from portal-ui-lib's `formatters.util.ts` with every
 * output made Danish: Angular rendered default dates `dd.MM.yyyy`, `short`/`long`/`datetime` in
 * en-GB, `currency` in US dollars, and plain numbers and percentages in en-US or the browser's
 * locale. Here dates are always `dd-MM-yyyy` (CLAUDE.md), times `HH:mm`, numbers `da-DK`, and
 * money is kroner.
 */

const da = (fractionDigits: { min: number; max: number }) =>
  new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: fractionDigits.min,
    maximumFractionDigits: fractionDigits.max,
  });

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isNaN(num) ? null : num;
}

/**
 * Tokens: `integer`, `decimal:N`, `percent` (0–2 decimals), `percent:N`, `currency` /
 * `currency-dkk` (2 decimals, kr.), `currency-dkk:N`. Percent values are already ×100 (12.3 → 12,3 %).
 */
export function formatCellNumber(value: unknown, format?: string): string {
  const num = toNumber(value);
  if (num === null) {
    return value === null || value === undefined ? '' : String(value);
  }
  if (!format) {
    return da({ min: 0, max: 2 }).format(num);
  }
  if (format === 'integer') {
    return da({ min: 0, max: 0 }).format(num);
  }
  const decimals = /^decimal:(\d+)$/.exec(format);
  if (decimals) {
    const n = Number(decimals[1]);
    return da({ min: n, max: n }).format(num);
  }
  if (format === 'percent') {
    return `${da({ min: 0, max: 2 }).format(num)} %`;
  }
  const percentFixed = /^percent:(\d+)$/.exec(format);
  if (percentFixed) {
    const n = Number(percentFixed[1]);
    return `${da({ min: n, max: n }).format(num)} %`;
  }
  if (format === 'currency' || format === 'currency-dkk') {
    return `${da({ min: 2, max: 2 }).format(num)} kr.`;
  }
  const currencyFixed = /^currency-dkk:(\d+)$/.exec(format);
  if (currencyFixed) {
    const n = Number(currencyFixed[1]);
    return `${da({ min: n, max: n }).format(num)} kr.`;
  }
  return da({ min: 0, max: 2 }).format(num);
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

const hhmm = (date: Date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

/** Tokens: none / `date-da` / `short` / `long` → `dd-MM-yyyy`; `datetime` / `datetime-da` → `dd-MM-yyyy HH:mm`; `time` → `HH:mm`. */
export function formatCellDate(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const date = toDate(value);
  if (!date) {
    return String(value);
  }
  if (format === 'datetime' || format === 'datetime-da') {
    return `${formatDkDate(date)} ${hhmm(date)}`;
  }
  if (format === 'time') {
    return hhmm(date);
  }
  return formatDkDate(date);
}

export function formatCellEnum(
  value: unknown,
  options: { value: string | number | boolean; label: string }[] = [],
): string {
  if (value === null || value === undefined) {
    return '';
  }
  return options.find(option => option.value === value)?.label ?? String(value);
}

/** Solid legacy tag colours (`bg-*` for `Chip`'s `className`), assigned in option order. */
const BADGE_COLORS = [
  'bg-info',
  'bg-brand',
  'bg-warning',
  'bg-danger',
  'bg-[#625e95]',
  'bg-neutral',
];

/** A stable badge colour per enum option, by its position (portal-ui-lib's `getEnumBadgeColor`). */
export function enumBadgeColor(
  value: unknown,
  options: { value: string | number | boolean }[] = [],
): string {
  const index = options.findIndex(option => option.value === value);
  return index === -1
    ? BADGE_COLORS[BADGE_COLORS.length - 1]!
    : BADGE_COLORS[index % BADGE_COLORS.length]!;
}
